import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import UserListForLesson from "./components/Admin/UserListForLesson";
import { getLessonTypeStyle } from "./utils/colorUtils";
import { getAuthHeaders } from "./utils/auth";
import "./assets/style/Timetable.css";


export const DeleteUsers = () => {
  const [lessons, setLessons] = useState([]);
  const [refreshUsers, setrefreshUsers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      const lessons_endpoint = `${process.env.REACT_APP_BACKEND_ADDRESS}/lessons_attendees/`;
      const lessonsResponse = await axios.get(lessons_endpoint, { headers });
      setLessons(lessonsResponse.data.lessons || []);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setError("Nie udało się pobrać danych o zajęciach");
    } finally {
      setLoading(false);
    }
  };

  // Get week boundaries
  const getWeekBoundaries = () => {
    const now = new Date();
    const currentDay = now.getDay();
    const diff = currentDay === 0 ? -6 : 1 - currentDay; // Days since Monday

    // Create new Date object without mutating
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    // Add week offset
    const weekStart = new Date(monday);
    weekStart.setDate(monday.getDate() + (weekOffset * 7));

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    return { weekStart, weekEnd };
  };

  // Filter lessons by week offset
  const getFilteredLessonsByWeek = () => {
    const { weekStart, weekEnd } = getWeekBoundaries();

    return lessons.filter(lesson => {
      if (!lesson.lesson?.flat_start) return false;
      const lessonDate = new Date(lesson.lesson.flat_start);
      return lessonDate >= weekStart && lessonDate <= weekEnd;
    });
  };

  // Format week range for display
  const getWeekRangeText = () => {
    const { weekStart, weekEnd } = getWeekBoundaries();

    const formatDate = (date) => {
      return date.toLocaleDateString('pl-PL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    };

    return `${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
  };

  // Fetch lessons whenever refreshUsers toggles
  React.useEffect(() => {
    fetchData();
  }, [refreshUsers]);

  const handleRefresh = () => {
    setrefreshUsers(!refreshUsers);
  };

  const addLeadingZeros = (num) => String(num).padStart(2, "0");

  // Helper function to organize lessons into timetable structure
  const organizeLessonsForTimetable = () => {
    const timetable = {};
    const filteredLessons = getFilteredLessonsByWeek();

    filteredLessons.forEach(lesson => {
      if (!lesson.lesson?.flat_start) return;

      const lessonDate = new Date(lesson.lesson.flat_start);
      const hours = lessonDate.getHours();
      const minutes = lessonDate.getMinutes();
      const timeSlot = `${addLeadingZeros(hours)}:${addLeadingZeros(minutes)}`;

      if (!timetable[timeSlot]) {
        timetable[timeSlot] = [];
      }

      // Add lesson with its week day from API
      timetable[timeSlot].push({
        ...lesson,
        week_day: lesson.lesson.week_day,
        time_slot: timeSlot,
      });
    });

    return timetable;
  };

  const formatLessonTime = (dateString) => {
    const date = new Date(dateString);
    return `${addLeadingZeros(date.getHours())}:${addLeadingZeros(date.getMinutes())}`;
  };

  // Component to render a single hour row in the timetable
  const SingleLessonHour = ({ hour, timeSlot }) => {
    const weekDayMap = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    const timeSlots = [];

    for (let i = 0; i < 7; i++) {
      const dayName = weekDayMap[i];
      const lessonsInDay = hour ? hour.filter(lesson => lesson.week_day === dayName) : [];

      if (lessonsInDay.length > 0) {
        const lessonGroup = lessonsInDay.map((lesson) => (
          <LessonCardInCell
            key={lesson.id}
            lesson={lesson}
            onRefresh={handleRefresh}
          />
        ));

        timeSlots.push(
          <td key={`lesson-group-${i}`} style={{ padding: '16px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
            {lessonGroup}
          </td>
        );
      } else {
        timeSlots.push(
          <td
            key={`empty-${i}`}
            style={{
              backgroundColor: '#f8f9fa',
              height: '120px',
              border: '1px solid #e0e0e0'
            }}
          />
        );
      }
    }

    return <tr>{timeSlots}</tr>;
  };

  // Component for individual lesson card in timetable cell
  const LessonCardInCell = ({ lesson, onRefresh }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [userListOpen, setUserListOpen] = useState(false);

    const handleUserAdded = () => {
      setUserListOpen(false);
      onRefresh();
    };

    return (
      <>
        <Box
          sx={{
            p: 2,
            mb: 1,
            borderRadius: 2,
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease'
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.9rem',
              textAlign: 'center',
              mb: 1,
              ...getLessonTypeStyle(lesson.lesson.lesson_type.bg_color),
            }}
          >
            {lesson.lesson.lesson_type.friendly_name || lesson.lesson.lesson_type.name}
          </Box>

          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, textAlign: 'center' }}>
            {formatLessonTime(lesson.lesson.flat_start)}
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
            Uczestników: {lesson.attendees?.length || 0}
          </Typography>

          <Box display="flex" gap={0.5} mb={1} justifyContent="center">
            <Button
              size="small"
              onClick={() => setUserListOpen(true)}
              startIcon={<PersonAddIcon />}
              color="success"
              variant="outlined"
              sx={{
                fontSize: '0.65rem',
                minWidth: 'auto',
                px: 0.5,
                py: 0.25,
                height: 24,
              }}
            >
              Dodaj
            </Button>
            <Button
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              color="primary"
              variant="outlined"
              sx={{
                fontSize: '0.65rem',
                minWidth: 'auto',
                px: 0.5,
                py: 0.25,
                height: 24,
              }}
            >
              {isExpanded ? 'Ukryj' : 'Pokaż'}
            </Button>
          </Box>

          {/* Expanded participant list */}
          <Collapse in={isExpanded}>
            <Divider sx={{ my: 1 }} />
            {lesson.attendees && lesson.attendees.length > 0 ? (
              <Box>
                {lesson.attendees.map((user) => (
                  <Box
                    key={user.id}
                    sx={{
                      p: 1,
                      mb: 0.5,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" fontWeight={500}>
                        {user.name}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteUser(user.id, lesson.id)}
                        sx={{
                          padding: '2px',
                          '& svg': { fontSize: '0.9rem' }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    {user.phone && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {user.phone}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
                Brak uczestników
              </Typography>
            )}
          </Collapse>
        </Box>

        {/* Add User Modal */}
        {userListOpen && (
          <UserListForLesson
            lessonId={lesson.id}
            onUserAdded={handleUserAdded}
            open={userListOpen}
            onClose={() => setUserListOpen(false)}
          />
        )}
      </>
    );
  };

  const handleDeleteUser = async (userId, lessonId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tego uczestnika z zajęć?')) {
      return;
    }

    try {
      const deleteEndpoint = `${process.env.REACT_APP_BACKEND_ADDRESS}/user_from_lesson/`;
      const headers = await getAuthHeaders();
      const httpReqHeaders = {
        headers,
        data: {
          lesson_id: lessonId,
          user_id: userId,
        },
      };

      await axios.delete(deleteEndpoint, httpReqHeaders);
      setSuccess("Uczestnik został usunięty z zajęć");
      handleRefresh();
    } catch (err) {
      console.error("Failed to delete user:", err);
      setError("Nie udało się usunąć uczestnika");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <Box textAlign="center">
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">Ładowanie zajęć...</Typography>
        </Box>
      </Box>
    );
  }

  const timetable = organizeLessonsForTimetable();
  const filteredLessons = getFilteredLessonsByWeek();
  const totalParticipants = filteredLessons.reduce((sum, lesson) => sum + (lesson.attendees?.length || 0), 0);

  // Mobile list view component
  const MobileListView = () => {
    const sortedLessons = [...filteredLessons].sort((a, b) => {
      const dateA = new Date(a.lesson.flat_start);
      const dateB = new Date(b.lesson.flat_start);
      return dateA - dateB;
    });

    // Group lessons by day
    const lessonsByDay = sortedLessons.reduce((acc, lesson) => {
      const day = lesson.lesson.week_day;
      if (!acc[day]) acc[day] = [];
      acc[day].push(lesson);
      return acc;
    }, {});

    const weekDayOrder = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];

    return (
      <Box>
        {weekDayOrder.map(day => {
          const dayLessons = lessonsByDay[day];
          if (!dayLessons || dayLessons.length === 0) return null;

          return (
            <Box key={day} sx={{ mb: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  pb: 1, 
                  borderBottom: '2px solid #667eea',
                  color: '#667eea',
                  fontWeight: 600
                }}
              >
                {day}
              </Typography>
              {dayLessons.map(lesson => (
                <LessonCardInCell
                  key={lesson.id}
                  lesson={lesson}
                  onRefresh={handleRefresh}
                />
              ))}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            Zarządzanie uczestnikami zajęć
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Zarządzaj uczestnikami zajęć w formacie tygodniowego grafiku
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, fontWeight: 500, color: 'primary.main' }}>
            {getWeekRangeText()}
          </Typography>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            onClick={() => setWeekOffset(weekOffset - 1)}
            variant="outlined"
          >
            Poprzedni tydzień
          </Button>
          <Button
            onClick={() => setWeekOffset(0)}
            variant="outlined"
            disabled={weekOffset === 0}
          >
            Obecny tydzień
          </Button>
          <Button
            onClick={() => setWeekOffset(weekOffset + 1)}
            variant="outlined"
          >
            Następny tydzień
          </Button>
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {/* Summary Card */}
      <Card sx={{ mb: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <PeopleIcon sx={{ fontSize: 48, color: "white" }} />
            <Box>
              <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                {totalParticipants}
              </Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)" }}>
                Uczestników zapisanych na zajęcia
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Timetable View */}
      {Object.keys(timetable).length > 0 ? (
        isMobile ? (
          <MobileListView />
        ) : (
          <Box sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            overflow: { xs: 'auto', sm: 'hidden' },
            backgroundColor: 'white'
          }}>
            <div id="timetable">
              <div className="table-responsive" style={{ overflowX: 'auto' }}>
                <table
                  className="table table-bordered text-center white-table"
                  style={{
                    marginBottom: 0,
                    minWidth: '800px'
                  }}
                >
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Poniedziałek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        PON
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Wtorek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        WT
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Środa
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        ŚR
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Czwartek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        CZ
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Piątek
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        PT
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Sobota
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        SO
                      </Box>
                    </th>
                    <th className="text-uppercase" style={{
                      padding: '16px',
                      fontWeight: 600,
                      color: '#333',
                      fontSize: '0.875rem',
                      minWidth: '100px'
                    }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Niedziela
                      </Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        ND
                      </Box>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(timetable)
                    .sort((a, b) => {
                      const [hourA, minuteA] = a.split(':').map(Number);
                      const [hourB, minuteB] = b.split(':').map(Number);

                      if (hourA !== hourB) {
                        return hourA - hourB;
                      }
                      return minuteA - minuteB;
                    })
                    .map((timeSlot) => {
                      const lessonsAtTime = timetable[timeSlot];
                      return (
                        <SingleLessonHour
                          key={timeSlot}
                          hour={lessonsAtTime}
                          timeSlot={timeSlot}
                        />
                      );
                    })}
                </tbody>
                </table>
              </div>
            </div>
          </Box>
        )
      ) : (
        <Box sx={{
          p: 6,
          textAlign: 'center',
          border: '2px dashed #e0e0e0',
          borderRadius: 2,
          backgroundColor: '#fafafa'
        }}>
          <ScheduleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Brak zajęć w wybranym tygodniu
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            W wybranym tygodniu nie ma zaplanowanych zajęć z uczestnikami.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
